import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import classes from './UploadRentRoll.module.css';
import { useFullScreenLoader } from '~/hooks/useFullScreenLoader';
import { withFullScreenLoader } from '~/hocs/withFullScreenLoader';
import { Building } from '~/types/building';
import { fetchBuildingDetails } from '~/api/building';
import { useParams } from 'react-router-dom';
import UploadRentRollCsv from './UploadRentRollCsv';
import ColumnMatch from './ColumnMatch';
import RentRollConfirmModal, { PreviewSummary } from './RentRollConfirmModal';
import RentRollImportsList, { RentRollImportRow } from './RentRollImportsList';
import { handleRentRollConfirmError } from './handleRentRollConfirmError';
import { uniqueId } from 'lodash';
import { useTenantCsvMapping } from '~/hooks/useTenantCsvMapping';
import {
  bulkMoveOutTenants,
  confirmRentRollImport,
  previewRentRoll
} from '~/api/resident';
import { toast } from 'react-toastify';
import { Colors } from '~/enums/Colors';

const UploadRentRoll = () => {
  const [parsedCsv, setparsedCsv] = useState<{
    rows: any[];
    maxColumns: number;
  }>();
  const [building, setbuilding] = useState<Building>();
  const [selectedStartingRow, setselectedStartingRow] = useState<any>();
  const { buildingId } = useParams<{ buildingId: string }>();
  const [currentStep, setCurrentStep] = useState<
    | 'UPLOAD_RENT_ROLL'
    | 'RENTROLL_COLUMN_SELECTION_TABLE'
    | 'RENTROLL_PREVIEW_CONFIRMATION'
    | 'UPLOAD_MOVEOUT_LIST'
    | 'MOVEOUT_COLUMN_SELECTION_TABLE'
    | ''
  >('');
  const [uploadModalVisible, setuploadModalVisible] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [previewResult, setPreviewResult] = useState<{
    previewId: string;
    summary: PreviewSummary;
  }>();

  const queryClient = useQueryClient();
  const {
    fieldMapping,
    columnNameSelected,
    getTargetedRows,
    prepareTenantInfo
  } = useTenantCsvMapping();

  const reset = () => setCurrentStep('');

  const openUploadRentrollCsvModal = () => {
    setuploadModalVisible(true);
    setCurrentStep('UPLOAD_RENT_ROLL');
  };
  const openUploadMoveoutCsvModal = () => {
    setuploadModalVisible(true);
    setCurrentStep('UPLOAD_MOVEOUT_LIST');
  };
  const closeUploadModal = () => {
    setCurrentStep('');
    setuploadModalVisible(false);
  };

  const { loader } = useFullScreenLoader();

  const getBuilding = () => {
    loader.show();
    fetchBuildingDetails(buildingId)
      .then((res) => {
        setbuilding(res.data);
      })
      .finally(() => loader.hide());
  };

  const previewMutation = useMutation(previewRentRoll, {
    onSuccess: (res) => {
      setPreviewResult(res.data);
      setCurrentStep('RENTROLL_PREVIEW_CONFIRMATION');
      // A new preview supersedes any prior pending one for this building
      // (expired server-side), so refresh the list to reflect that.
      queryClient.invalidateQueries('rentRollImports');
    },
    onError: (err: any) => {
      toast.error(
        err?.message || 'Failed to generate preview. Please try again.'
      );
    }
  });

  const confirmMutation = useMutation(confirmRentRollImport, {
    onSuccess: (res) => {
      toast.success(res.data?.message);
      displayTenantsImportSuccessAlert(res.data?.result);
      setCurrentStep('');
      setPreviewResult(undefined);
      setparsedCsv(undefined);
      setSelectedFile(undefined);
      queryClient.invalidateQueries('rentRollImports');
    },
    onError: (err: any) => {
      const action = handleRentRollConfirmError(err);
      if (action.clearPreview) {
        const canReturnToColumnMapping =
          action.returnToColumnMapping && !!parsedCsv;
        setCurrentStep(
          canReturnToColumnMapping ? 'RENTROLL_COLUMN_SELECTION_TABLE' : ''
        );
        setPreviewResult(undefined);
        queryClient.invalidateQueries('rentRollImports');
      }
    }
  });

  useEffect(() => {
    if (previewMutation.isLoading || confirmMutation.isLoading) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [previewMutation.isLoading, confirmMutation.isLoading]);

  const onUploadSuccess = ({
    rows,
    maxColumns,
    file
  }: {
    rows: any[];
    maxColumns: number;
    file: File;
  }) => {
    const rowsWithId = rows?.map((row) => {
      row.id = uniqueId();
      return row;
    });

    setparsedCsv({
      rows: rowsWithId,
      maxColumns
    });
    setSelectedFile(file);

    setCurrentStep('RENTROLL_COLUMN_SELECTION_TABLE');
  };

  const onMoveOutListParsed = ({
    rows,
    maxColumns
  }: {
    rows: any[];
    maxColumns: number;
  }) => {
    const rowsWithId = rows?.map((row) => {
      row.id = uniqueId();
      return row;
    });

    setparsedCsv({
      rows: rowsWithId,
      maxColumns
    });

    setCurrentStep('MOVEOUT_COLUMN_SELECTION_TABLE');
  };

  const onStartingRowSelected = (row: any) => {
    setselectedStartingRow(row);
    const element = document.getElementById(`column-match-radio-${row.id}`);

    if (element?.attributes) {
      // @ts-ignore
      element.checked = true;

      // element.attributes['checked'] = true;
    }
  };

  const displayTenantsImportSuccessAlert = (result: any) => {
    alert(
      `Tenants successfully imported.
      \n Existing Leads - ${result?.intercomExistingLeads?.length ?? 0}
      \n Leads created - ${result?.intercomLeadsCreated?.length ?? 0}`
    );
  };

  const displayMoveoutSuccessAlert = (result: any) => {
    alert(
      `Tenants successfully moved out.
      \n Move outs - ${result?.success?.length ?? 0}`
    );
  };

  const triggerPreview = () => {
    if (!selectedStartingRow?.id) {
      return toast.error('Starting row not selected');
    }

    if (!selectedFile) {
      return toast.error('No file found — please re-upload.');
    }

    try {
      const targetRows = getTargetedRows(
        parsedCsv?.rows ?? [],
        selectedStartingRow.id
      );

      const tenants = prepareTenantInfo({
        rows: targetRows,
        buildingId: buildingId
      });

      previewMutation.mutate({ file: selectedFile, tenants, buildingId });
    } catch (error) {
      console.error(error);
    }
  };

  const uploadMoveoutList = () => {
    try {
      if (!selectedStartingRow?.id) {
        return toast.error('Starting row not selected');
      }

      const targetRows = getTargetedRows(
        parsedCsv?.rows ?? [],
        selectedStartingRow.id
      );

      const tenants = prepareTenantInfo({
        rows: targetRows,
        buildingId: buildingId
      });

      loader.show();

      bulkMoveOutTenants({
        building: buildingId,
        moveOuts: tenants
      })
        .then((res) => {
          const { result } = res.data;
          toast.success(res.data?.message);
          displayMoveoutSuccessAlert(result);
          setCurrentStep('');
        })
        .finally(() => {
          loader.hide();
        });
    } catch (error) {
      console.error(error);
    }
  };

  const onReviewPendingPreview = (preview: RentRollImportRow) => {
    setPreviewResult({ previewId: preview.uuid, summary: preview.summary });
    setCurrentStep('RENTROLL_PREVIEW_CONFIRMATION');
  };

  useEffect(() => {
    getBuilding();
  }, []);

  return (
    <div className={classes.pageWrapper}>
      {['', 'UPLOAD_RENT_ROLL', 'UPLOAD_MOVEOUT_LIST'].includes(
        currentStep
      ) && (
        <div
          className={`flex-row align-start justify-between ${classes.headerWrapper}`}
        >
          <div>
            <div
              className='margin-bottom-16'
              style={{ fontSize: '32px', color: Colors.cobuDarkBlue }}
            >
              Tenant Management in <b>{building?.name}</b>
            </div>
            <div
              className='margin-top-16 margin-bottom-8'
              style={{ fontSize: '32px', color: Colors.cobuDarkBlue }}
            >
              Rent Roll
            </div>
            <p>
              From here, you can transfer, move in and move out residents in
              their respective properties. If there are no residents listed,
              either check their PMS Integration settings, or upload a new rent
              roll file.
            </p>
          </div>
          <div className='flex-row justify-end'>
            <div className='margin-right-16'>
              <button
                className='btn-standard btn-primary btn-small'
                onClick={openUploadRentrollCsvModal}
              >
                Upload Rent Roll
              </button>
              <p>Transfers, Move In, And Move Out</p>
            </div>
            <div>
              <button
                className='btn-standard btn-danger btn-small'
                onClick={openUploadMoveoutCsvModal}
              >
                Upload Move Out List
              </button>
              <p>ONLY Move Out</p>
            </div>
          </div>
        </div>
      )}
      {['', 'UPLOAD_RENT_ROLL', 'UPLOAD_MOVEOUT_LIST'].includes(currentStep) &&
        buildingId && (
          <RentRollImportsList
            buildingId={buildingId}
            onReview={onReviewPendingPreview}
          />
        )}
      <UploadRentRollCsv
        isVisible={uploadModalVisible && currentStep == 'UPLOAD_RENT_ROLL'}
        onClose={closeUploadModal}
        onUpload={onUploadSuccess}
        title='Upload Rent Roll'
      />
      <UploadRentRollCsv
        isVisible={uploadModalVisible && currentStep == 'UPLOAD_MOVEOUT_LIST'}
        onClose={closeUploadModal}
        onUpload={onMoveOutListParsed}
        title='Upload Moveouts'
      />
      {currentStep == 'RENTROLL_COLUMN_SELECTION_TABLE' && parsedCsv && (
        <ColumnMatch
          rows={parsedCsv.rows}
          fieldMapping={fieldMapping}
          maxColumns={parsedCsv.maxColumns}
          onRowSelection={onStartingRowSelected}
          selectedRow={selectedStartingRow}
          buildingId={buildingId}
          onUpload={triggerPreview}
          onColumnNameSelected={columnNameSelected}
          onCancel={reset}
          uploadActionTitle={'Upload Residents'}
        />
      )}
      {previewResult && (
        <RentRollConfirmModal
          isVisible={currentStep === 'RENTROLL_PREVIEW_CONFIRMATION'}
          summary={previewResult.summary}
          isConfirming={confirmMutation.isLoading}
          onConfirm={() => confirmMutation.mutate(previewResult.previewId)}
          onCancel={() => {
            setCurrentStep(parsedCsv ? 'RENTROLL_COLUMN_SELECTION_TABLE' : '');
            setPreviewResult(undefined);
          }}
        />
      )}
      {currentStep == 'MOVEOUT_COLUMN_SELECTION_TABLE' && parsedCsv && (
        <ColumnMatch
          rows={parsedCsv.rows}
          fieldMapping={fieldMapping}
          maxColumns={parsedCsv.maxColumns}
          onRowSelection={onStartingRowSelected}
          selectedRow={selectedStartingRow}
          buildingId={buildingId}
          onUpload={uploadMoveoutList}
          onColumnNameSelected={columnNameSelected}
          onCancel={reset}
          uploadActionTitle={'Moveout Residents'}
        />
      )}
    </div>
  );
};

export default withFullScreenLoader(UploadRentRoll);
